import _get from "lodash/get";
import { AnyAction, Dispatch, Middleware } from "redux";
import { GENERAL_CLEAR_FLASH } from "./actionTypes";
import { showGlobalModal, setNegativeFlash, setInfoFlash, setWarningFlash } from "./generalActions";
import { isStartingAjaxCall, isFailedAjaxCall } from "../utils";

/**
 * Middleware that does:
 * 1. Clear the currently displaying error (if any) before a remote call because the call is
 *    expected to either fix the problem or produce a new one
 * 2. If the remote call fails and has any user-displayable error, display it
 * 3. If the user has invalid, locked or none order, notify her
 */
export default function asyncErrorHandlingMiddleware(): Middleware {
    return () => (next) => (action: AnyAction) => {
        if (isStartingAjaxCall(action)) {
            next({ type: GENERAL_CLEAR_FLASH });
        }

        if (isFailedAjaxCall(action)) {
            const error = action.payload;

            if (error.code === "InvalidOrder") {
                next(showGlobalModal("invalidOrder"));
            } else if (error.code === "LockedOrder") {
                next(showGlobalModal("lockedOrder"));
            } else if (error.code === "NoneOrder") {
                next(showGlobalModal("noneOrder"));
            } else if (error.code === "FinishedOrderLackingSubmit") {
                window.location.assign("/shop/kasse/resubmit/");
            } else {
                dispatchSetFlash({ next, action, error });
            }
        }

        return next(action);
    };
}

export interface ErrorParams {
    code: string;
    displayableMessage?: string;
    isGenericMessage?: boolean;
    /** Optional severity of the displayable error, e.g. "info"/"warning" to show it as a notice/warning instead of an error. */
    severity?: "error" | "info" | "warning";
}

const FLASH_SETTER_BY_SEVERITY = {
    error: setNegativeFlash,
    info: setInfoFlash,
    warning: setWarningFlash,
};

function dispatchSetFlash({ next, action, error }: { action: AnyAction; error: ErrorParams; next: Dispatch }) {
    if (_get(action, "meta.flashOnError") === false) {
        return;
    }
    const message = error.isGenericMessage
        ? _get(action, "meta.flashOnError.defaultErrorMessage", error.displayableMessage)
        : error.displayableMessage;

    const gaEvent =
        _get(action.meta, "gaEvent") || // optional custom GA event name
        action.type.replace("_REJECTED", ""); // Remove the suffix added by the promise middleware

    const setFlashAction = FLASH_SETTER_BY_SEVERITY[error.severity || "error"] || setNegativeFlash;
    next(setFlashAction(message, { gaEvent }));
}
